import React from 'react';
import PropTypes from 'prop-types';
import { useMatch } from 'react-router-dom';

import { MANAGE_SERVICES_TAB } from 'constants/logic';
import AutoSyncStatus from 'components/AutoSyncStatus';
import ComponentErrorBoundary from 'components/ComponentErrorBoundary';
import FeatureNotSupported from 'components/FeatureNotSupported';
import Message from 'components/Message';
import When from 'components/When';
import { Title, Controls, Control, ColumnLayout, Column } from 'features/App';
import { bem } from 'helpers/class-name';
import { useIsServicePricingProofEnabled } from 'hooks/useIsServicePricingProofEnabled';

import { ServicesComponents } from './Services';
import { ResourcesComponents } from './Resources';
import { PractitionersComponents } from './Practitioners';
import ClassList from './ClassList/ClassesListContainer';
import ListLink from './Components/ListLink/ListLinkContainer';
import PricingActionRequiredBanner from './Components/PricingActionRequiredBanner';
import IntegratedServiceDetail from './IntegratedServiceDetail';
import messages from './ManageServices.messages';
import './ManageServices.scss';

const { block } = bem('manage-services');

const ManageServices = ({
  activeTab,
  isMsInventoryVenue,
  isAppointmentTypeVenue,
  showAutoSyncStatus,
  featureIsSupported,
  isServicesEmpty,
  isPractitionersEmpty,
  isResourcesEmpty,
}) => {
  const isAddRoute = !!useMatch('/manage/services/:venueId/:tab/add');
  const isEditRoute = !!useMatch('/manage/services/:venueId/:tab/:itemId/edit');
  const itemRouteMatch = useMatch('/manage/services/:venueId/:tab/:itemId');
  const isDetailsRoute = !isAddRoute && !isEditRoute;

  const isPricingProofEnabled = useIsServicePricingProofEnabled();
  const integratedDetailEnabled = isPricingProofEnabled === true && isAppointmentTypeVenue;
  // The ':itemId' match also catches the 'add' sentinel, so exclude it.
  const selectedClassId = isAddRoute ? undefined : itemRouteMatch?.params?.itemId;

  let components;
  let titleMessage;
  let isEmpty;

  switch (activeTab) {
    case MANAGE_SERVICES_TAB.PRACTITIONERS:
      components = PractitionersComponents;
      titleMessage = messages.practitioners;
      isEmpty = isPractitionersEmpty;
      break;
    case MANAGE_SERVICES_TAB.SERVICES:
      components = ServicesComponents;
      titleMessage = messages.services;
      isEmpty = isServicesEmpty;
      break;
    case MANAGE_SERVICES_TAB.RESOURCES:
    default:
      components = ResourcesComponents;
      titleMessage = messages.resources;
      isEmpty = isResourcesEmpty;
      break;
  }

  if (!featureIsSupported)
    return (
      <ColumnLayout>
        <Column>
          <FeatureNotSupported />
        </Column>
      </ColumnLayout>
    );

  if (!isMsInventoryVenue) {
    const showIntegratedDetail = integratedDetailEnabled && !!selectedClassId;

    return (
      <ColumnLayout dataCypress="classes-list">
        <Column>
          {showIntegratedDetail ? (
            <IntegratedServiceDetail />
          ) : (
            <>
              <PricingActionRequiredBanner />
              <ClassList linkClasses={integratedDetailEnabled} />
            </>
          )}
        </Column>
      </ColumnLayout>
    );
  }

  if (!isAddRoute && !isEditRoute && isEmpty) {
    return (
      <ColumnLayout>
        <Column>
          <Controls>
            {showAutoSyncStatus && (
              <Control type="trough">
                <AutoSyncStatus />
              </Control>
            )}

            <Control type="button">
              <components.addButton />
            </Control>
          </Controls>
          <components.empty />
        </Column>
      </ColumnLayout>
    );
  }

  if (isAddRoute || isEditRoute) {
    return (
      <ColumnLayout>
        <Column type="standard">
          <ListLink />
          <When predicate={isAddRoute}>
            <components.add />
          </When>
          <When predicate={isEditRoute}>
            <components.edit />
          </When>
        </Column>
      </ColumnLayout>
    );
  }

  return (
    <ComponentErrorBoundary>
      <ColumnLayout className={block()} dataCypress="manage-services">
        <Column className="column-layout__column--lg-4/12" name="list" type="flush">
          <Message message={titleMessage} as={Title} />

          <Controls>
            {showAutoSyncStatus && (
              <Control type="trough">
                <AutoSyncStatus />
              </Control>
            )}

            <Control type="trough">
              {isDetailsRoute && (
                <Controls inline>
                  <Control type="button">
                    <components.addButton />
                  </Control>
                </Controls>
              )}
            </Control>
          </Controls>

          <PricingActionRequiredBanner />

          {isMsInventoryVenue && <components.list />}
        </Column>
        <Column className="column-layout__column--lg-8/12 lg-ml-l mb-l" name="details">
          {isMsInventoryVenue && isAddRoute && <components.add />}
          {isMsInventoryVenue && isEditRoute && <components.edit />}
          {isMsInventoryVenue && isDetailsRoute && <components.details />}
        </Column>
      </ColumnLayout>
    </ComponentErrorBoundary>
  );
};

ManageServices.propTypes = {
  activeTab: PropTypes.string,
  isMsInventoryVenue: PropTypes.bool,
  isAppointmentTypeVenue: PropTypes.bool,
  showAutoSyncStatus: PropTypes.bool,
  featureIsSupported: PropTypes.bool,
  isServicesEmpty: PropTypes.bool,
  isPractitionersEmpty: PropTypes.bool,
  isResourcesEmpty: PropTypes.bool,
};

export default ManageServices;
